import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { PendingScreen } from "./components/pending-screen";
import { routeTree } from "./routeTree.gen";
import { registerFirmExit } from "./lib/firm-exit";
import { forgetFirm } from "./lib/leave-firm";
import { queryClient } from "./lib/query-client";
import { sceneTransition } from "./lib/scene-transition";
import "./styles.css";

const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultViewTransition: false,
  // Routes decide who may see them before they render, so a wait can precede any screen. A quick
  // answer shows nothing at all; a slow one shows the loader, and keeps it long enough to read
  // rather than flashing it for a frame.
  defaultPendingComponent: () => <PendingScreen />,
  defaultPendingMs: 150,
  defaultPendingMinMs: 400,
});

// Someone who is no longer in the firm (removed while this browser was open) is taken to the
// screen for people with none, with nothing of the firm left behind.
registerFirmExit(() => {
  forgetFirm();
  void router.navigate({ to: "/onboarding", replace: true });
});

// Honor live preference changes and keep keyboard navigation immediate. Native
// transitions are progressive: TanStack navigates normally when the API is absent.
let keyboardNavigation = false;
let initialRouteResolved = false;
function updateMotionPreference() {
  const enabled = initialRouteResolved && !motionPreference.matches && !keyboardNavigation;
  // Enabled means a scene change gets the transition (see `sceneTransition`); the same object each
  // time, so a preference that did not change does not update the router.
  const wanted = enabled ? sceneTransition : false;
  if (router.options.defaultViewTransition !== wanted)
    router.update({ defaultViewTransition: wanted });
}
// The first paint has no previous page to bridge. Waiting for its resolution
// also avoids capturing a session loader over freshly editable form fields.
const stopInitialResolution = router.subscribe("onResolved", () => {
  initialRouteResolved = true;
  updateMotionPreference();
  stopInitialResolution();
});
function setKeyboardNavigation() {
  keyboardNavigation = true;
  updateMotionPreference();
}
function setPointerNavigation() {
  keyboardNavigation = false;
  updateMotionPreference();
}
motionPreference.addEventListener("change", updateMotionPreference);
document.addEventListener("keydown", setKeyboardNavigation, true);
document.addEventListener("pointerdown", setPointerNavigation, true);
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopInitialResolution();
    motionPreference.removeEventListener("change", updateMotionPreference);
    document.removeEventListener("keydown", setKeyboardNavigation, true);
    document.removeEventListener("pointerdown", setPointerNavigation, true);
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const element = document.getElementById("root");
if (!element) throw new Error("Missing application root");

createRoot(element).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
